import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import '../assets/css/allpages.css';
import facebookIcon from '../assets/img/social/facebook.webp';
import googleIcon from '../assets/img/social/google.png';
import CustomInput from '../components/common/CustomInput';
import PhoneInputGroup from '../components/common/PhoneInputGroup';
import StaticPageHeader from '../components/common/StaticPageHeader';
import { callGetWebservice, callPostWebservice } from '../hooks/api';
import useFirebaseTokenUpdate from '../hooks/useFirebaseTokenUpdate';
import { setUser } from '../store/reducers/auth';
import { userData } from '../utils/constants';
import { saveToLocalStorage } from '../utils/localStorageUtils';
import { getParsedPhone, validatePhone } from '../utils/Utilities';

const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});


const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { updateFirebaseTokenAPI } = useFirebaseTokenUpdate();

    const [countries, setCountries] = useState([]);

    useEffect(() => {
        callGetWebservice('countries', {}, (response) => {
            setCountries(response.data);
        });
    }, []);

    const regValidationSchema = React.useMemo(() => Yup.object({
        fullName: Yup.string().min(3, 'Full Name must be at least 3 characters').required('Full Name is required'),
        email: Yup.string().email('Invalid email address').required('Email is required'),
        password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
        confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match').required('Confirm Password is required'),
        countryId: Yup.string().required('Country is required'),
        phoneNumber: Yup.string()
            .required('Phone Number is required')
            .test('is-valid-phone', 'Use a national or international number valid for the selected country.', function (value) {
                const { countryId } = this.parent;
                if (!countryId || !value) return true;
                const country = countries.find(c => c.id === countryId);
                if (country) {
                    return validatePhone(value, country.iso2);
                }
                return true;
            }),
    }), [countries]);

    const formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            const requestPacket = { email: values.email, password: values.password };
            callPostWebservice('login', requestPacket, (response) => {
                dispatch(setUser(response.data));
                saveToLocalStorage(userData, response.data);
                navigate('/');
                updateFirebaseTokenAPI(true);
                setSubmitting(false);
            }, (error) => {
                setSubmitting(false);
                formik.setErrors({ email: error.errMessage });
            });
        },
    });

    const formikReg = useFormik({
        initialValues: { fullName: '', email: '', password: '', confirmPassword: '', countryId: '', phoneNumber: '' },
        validationSchema: regValidationSchema,
        onSubmit: (values, { setSubmitting }) => {
            const country = countries.find(c => c.id === values.countryId);
            const parsedPhone = country ? getParsedPhone(values.phoneNumber, country.iso2) : null;
            const formattedPhone = parsedPhone ? parsedPhone.number : values.phoneNumber;

            const requestPacket = {
                name: values.fullName, email: values.email, password: values.password,
                password_confirmation: values.confirmPassword, country_id: values.countryId, phone_number: formattedPhone,
            };
            callPostWebservice('register', requestPacket, (response) => {
                dispatch(setUser(response.data));
                saveToLocalStorage(userData, response.data);
                navigate('/');
                updateFirebaseTokenAPI(true);
                setSubmitting(false);
            }, (error) => {
                setSubmitting(false);
                formikReg.setErrors({ email: error.errMessage });
            });
        },
    });

    const onSocialLoginClick = (provider) => {
        const requestType = 'Website';
        callGetWebservice('social-auth/' + provider + '/redirect?request_type=' + requestType, {}, (response) => {
            window.location.href = response.data
        });
    }

    const countryOptions = countries.map(country => ({
        value: country.id, label: country.name, iso2: country.iso2, phone_code: country.phone_code
    }));

    return (
        <div className="static-page-wrapper">
            <StaticPageHeader title="Login & Register" />

            <div className="container static-page-container mt-4 mb-5">
                <div className="row align-items-start justify-content-center gap-4 gap-lg-0">

                    {/* Login Column */}
                    <div className="col-xl-5 col-lg-5 col-md-12 col-sm-12 animate__animated animate__fadeInUp">
                        <div className="glass-card p-4 p-md-5 h-100 shadow-sm border-0">
                            <div className="text-center mb-5">
                                <h2 className="font-heading ft-bold mb-2">Welcome Back</h2>
                                <p className="text-muted">Sign in to your account</p>
                            </div>
                            <form onSubmit={formik.handleSubmit}>
                                <CustomInput label="Email *" name="email" placeholder="Email" formik={formik} />
                                <CustomInput label="Password *" name="password" type="password" placeholder="Password" formik={formik} />

                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="flex-1 d-flex align-items-center">
                                        <input id="dd" className="checkbox-custom" name="dd" type="checkbox" onChange={formik.handleChange} />
                                        <label htmlFor="dd" className="checkbox-custom-label ms-2 mb-0" style={{ cursor: 'pointer' }}>Remember Me</label>
                                    </div>
                                    <div>
                                        <a href="login.html#" className="text-muted text-decoration-none" style={{ fontWeight: '600', fontSize: '0.9rem' }}>Lost Password?</a>
                                    </div>
                                </div>

                                <button type="submit" className="btn-product-action btn-cart-premium w-100 mt-3" disabled={formik.isSubmitting || !formik.isValid} style={{ position: 'relative', overflow: 'hidden' }}>
                                    <span style={{ visibility: formik.isSubmitting ? 'hidden' : 'visible' }}>Login <i className="fas fa-sign-in-alt ms-2"></i></span>
                                    {formik.isSubmitting && (
                                        <div className="progress" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '14px', backgroundColor: '#e2e8f0' }}>
                                            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: '100%', height: '100%', backgroundColor: '#0f172a' }}></div>
                                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '600', letterSpacing: '0.5px' }}>
                                                Authenticating...
                                            </div>
                                        </div>
                                    )}
                                </button>
                            </form>

                            <div className="text-center mt-5 mb-4">
                                <p className="text-muted" style={{ fontSize: '0.9rem' }}>or continue with</p>
                            </div>

                            <div className="d-flex justify-content-center gap-4">
                                <button className="btn btn-outline-dark d-flex align-items-center justify-content-center py-2 px-4" style={{ borderRadius: '14px', borderWidth: '2px', minWidth: '160px' }} onClick={() => onSocialLoginClick('facebook')}>
                                    <img src={facebookIcon} alt="Facebook" style={{ width: '22px', marginRight: '12px' }} />
                                    Facebook
                                </button>
                                <button className="btn btn-outline-dark d-flex align-items-center justify-content-center py-2 px-4 ml-2" style={{ borderRadius: '14px', borderWidth: '2px', minWidth: '160px' }} onClick={() => onSocialLoginClick('google')}>
                                    <img src={googleIcon} alt="Google" style={{ width: '22px', marginRight: '12px' }} />
                                    Google
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Spacer / Divider for Large Screens */}
                    <div className="col-lg-1 d-none d-lg-flex justify-content-center">
                        <div className="vr" style={{ opacity: '0.1', width: '2px', backgroundColor: '#000', borderRadius: '5px' }}></div>
                    </div>

                    {/* Register Column */}
                    <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 animate__animated animate__fadeInUp animate__delay-1s">
                        <div className="glass-card p-4 p-md-5 h-100 shadow-sm border-0">
                            <div className="text-center mb-5">
                                <h2 className="font-heading ft-bold mb-2">Create Account</h2>
                                <p className="text-muted">Join us for a premium shopping experience</p>
                            </div>
                            <form onSubmit={formikReg.handleSubmit}>
                                <CustomInput label="Full Name *" name="fullName" placeholder="Full Name" formik={formikReg} />
                                <CustomInput label="Email *" name="email" placeholder="Email" formik={formikReg} />

                                <div className="row">
                                    <div className="col-md-6"><CustomInput label="Password *" name="password" type="password" placeholder="Password" formik={formikReg} /></div>
                                    <div className="col-md-6"><CustomInput label="Confirm Password *" name="confirmPassword" type="password" placeholder="Confirm Password" formik={formikReg} /></div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <PhoneInputGroup
                                            formik={formikReg}
                                            countryName="countryId"
                                            phoneName="phoneNumber"
                                            countries={countries}
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="btn-product-action btn-cart-premium w-100 mt-3" disabled={formikReg.isSubmitting || !formikReg.isValid} style={{ position: 'relative', overflow: 'hidden' }}>
                                    <span style={{ visibility: formikReg.isSubmitting ? 'hidden' : 'visible' }}>Create An Account <i className="fas fa-user-plus ms-2"></i></span>
                                    {formikReg.isSubmitting && (
                                        <div className="progress" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '14px', backgroundColor: '#e2e8f0' }}>
                                            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: '100%', height: '100%', backgroundColor: '#0f172a' }}></div>
                                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '600', letterSpacing: '0.5px' }}>
                                                Creating Account...
                                            </div>
                                        </div>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default Login;
